"use client";

import React, { useEffect, useMemo, MouseEvent, useRef, useState, useCallback } from "react";
import {
  Flex,
  Box,
  openIntercom,
  Icon,
  Text,
  Button,
  Asset,
  AssetType,
  formatDate,
  SearchFilterInput,
  TableCell,
  EmptyState,
  usePaste,
  useContextMenu,
  ItemType,
} from "@thenounproject/lingo-core";
import Head from "next/head";
import styled from "styled-components";
import AssetLibraryHeader from "../components/AssetLibraryHeader";
import { useAssetLibraryContext } from "../../../contexts/AssetLibraryProvider";
import { canCreateAssets, canViewAssetLibrary } from "../utils";
import { useSelectSpace } from "@selectors/entities/spaces";
import { useSelectSpaceFields } from "@selectors/entities/fields";
import useCustomFields from "@actions/fields/useCustomFields";
import AssetLibrarySelectAll from "../components/AssetLibrarySelectAll";
import { NavPointTypes } from "@redux/navPoint/NavPoint";
import useNavPoint from "@hooks/useNavPoint";
import DataTable, { dataTableRowHeights } from "../../../components/table/DataTable";
import useLibraryAssets from "@redux/actions/search/useLibraryAssets";
import { useSelectionActions } from "@contexts/SelectedItemsProvider";
import formatAssetCustomFieldValue from "../formatAssetCustomFieldValue";

import formatBytes from "@helpers/bytesToSize";
import { TableColumn } from "../../../components/table";
import { AssetPolling } from "@components/assetDisplay/PollingWrappers";
import assetIconMap from "@components/assetDisplay/assetIconMap";
import getAssetBackground from "@helpers/getAssetBackground";
import { useAssetSelection } from "@redux/actions/assets/useAssetSelection";
import { useAssetPaste } from "@features/asset-paste";
import AssetLibraryActionMenu from "../components/AssetLibraryContextMenu";
import AssetLibraryInspectorView from "../components/AssetLibraryInspectorView";
import useCreateFileAssets from "@redux/actions/items/useCreateFileAssets";
import { getFilesFromDataTransfer } from "@helpers/getFilesFromDataTransfer";

type ScrollContainerProps = {
  rowSize?: number;
  height?: string;
};

const ScrollContainer = styled(Box).attrs<ScrollContainerProps>(({ rowSize, height }) => ({
  height: height ? height : `calc(100% + ${rowSize})px`,
}))<ScrollContainerProps>`
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;
`;

const DropOverlay = styled(Flex).attrs({
  position: "fixed",
  top: "0",
  left: "72px",
  right: "0",
  bottom: "0",
  zIndex: "1001",
  background: "modalOverlay",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
})<{ isVisible: boolean }>`
  opacity: ${props => (props.isVisible ? 1 : 0)};
  pointer-events: ${props => (props.isVisible ? "all" : "none")};
  transition: opacity 0.2s ease-in-out;
`;

export function renderEmptyState(appliedFilters: SearchFilterInput[]) {
  if (!appliedFilters.length) {
    return (
      <EmptyState
        title="Your asset library is empty"
        subtitle="Click the add button or add assets to a kit"
        iconProps={{ iconId: "empty-state.assets", size: "60", fill: "gray" }}
      />
    );
  }
  return (
    <EmptyState
      title="No assets match your query"
      subtitle="Click the active filters to remove them"
      iconProps={{ iconId: "empty-state.assets", size: "60", fill: "gray" }}
    />
  );
}

const AssetLibraryView = () => {
  const [rowSize, setRowSize] = React.useState<number>(0);
  const navPoint = useNavPoint();
  const [isDragging, setIsDragging] = useState(false);
  const tableWrapperRef = useRef(null);

  const space = useSelectSpace(),
    { isSelected: isSelectedImperative } = useSelectionActions(),
    { appliedFilters, assetRowSize, sort, setSortBy, showTableCustomFields } =
      useAssetLibraryContext(),
    { data, isLoading, fetchNextPage, error } = useLibraryAssets(
      { spaceId: space.id, filters: appliedFilters, sort },
      { refetchOnMount: true }
    );

  useCustomFields({
    spaceId: space?.id,
    kitId: navPoint?.kitId,
    portalId: navPoint?.portalId,
    assetId: navPoint?.assetId,
    assetToken: navPoint?.assetToken,
  });
  const fields = useSelectSpaceFields();
  const canViewLibrary = canViewAssetLibrary(space);
  const canCreateContent = canCreateAssets(space);
  const hasNextPage = data?.assets?.length < data?.total;

  const [createFileAssets] = useCreateFileAssets();

  const tableColumns = useMemo(() => {
    const cols: TableColumn<Asset>[] = [
      {
        name: "Name",
        minWidth: 350,
        renderer: asset => {
          return (
            <AssetPolling asset={asset}>
              <TableCell
                data={asset}
                space={space}
                rowSize={assetRowSize}
                icon={assetIconMap[asset.type]}
                imageBackground={getAssetBackground(asset, space.theme)}
                insights={false}
              />
            </AssetPolling>
          );
        },
        sortKey: "alpha",
      },
      {
        name: "Type",
        minWidth: 100,
        renderer: asset => <Text>{AssetType.displayName(asset?.type)}</Text>,
        sortKey: "type",
      },
      {
        name: "File Size",
        minWidth: 100,
        renderer: asset => <Text>{formatBytes(asset.size)}</Text>,
        sortKey: "size",
      },
      {
        name: "Resolution",
        minWidth: 100,
        renderer: asset => <Text>{asset.dimensions}</Text>,
        sortKey: "resolution",
      },
      {
        name: "Date Created",
        minWidth: 100,
        renderer: asset => <Text>{formatDate(asset.dateAdded, "MMM do, yyyy")}</Text>,
        sortKey: "recent",
      },
    ];
    if (showTableCustomFields) {
      for (const field of fields) {
        cols.push({
          name: field.name,
          minWidth: 120,
          flex: 1,
          renderer: asset => <Text>{formatAssetCustomFieldValue(field, asset)}</Text>,
        });
      }
    }
    return cols;
  }, [space, assetRowSize, showTableCustomFields, fields]);

  const { selectAsset, isSelected, onClick, disabled, CheckboxCell } = useAssetSelection({
    assets: data?.assets,
  });

  const selectionProps = useMemo(
    () => ({ isSelected, onClick, disabled, CheckboxCell }),
    [isSelected, onClick, disabled, CheckboxCell]
  );

  const { contextMenuXPos, contextMenuYPos, contextMenuOpen, handleContextMenuClose } =
    useContextMenu({
      itemRef: tableWrapperRef,
      onOpenMenu: event => {
        let current = event.target as HTMLElement;
        let assetId: string;

        // find the table row with asset data
        while (current && !assetId) {
          const dataIndex = current.dataset.index;
          if (dataIndex !== undefined) {
            const asset = data?.assets?.[parseInt(dataIndex)];
            if (asset) {
              assetId = asset.uuid;
            }
            break;
          }
          current = current.parentElement;
        }

        if (!assetId) return false;

        if (!isSelectedImperative("assetLibrary", assetId)) {
          const asset = data?.assets.find(a => a.uuid === assetId);
          if (asset) {
            selectAsset(asset, event as unknown as MouseEvent);
          }
        }
        return true;
      },
    });

  useEffect(() => {
    setRowSize(dataTableRowHeights[assetRowSize]);
  }, [assetRowSize]);

  const { pasteHandler } = useAssetPaste({
    insertConfig: {
      spaceId: space.id,
    },
  });

  usePaste(pasteHandler, !canViewLibrary || navPoint?.type === NavPointTypes.LibraryAsset);

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      if (!canCreateContent) return;
      const hasFiles = e.dataTransfer?.types?.includes("Files");
      if (hasFiles) {
        setIsDragging(true);
      }
    },
    [canCreateContent]
  );

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      handleDragOver(e);
    },
    [handleDragOver]
  );

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (e.clientX === 0 && e.clientY === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (!canCreateContent) return;

      const files = e.dataTransfer ? await getFilesFromDataTransfer(e.dataTransfer) : [];
      if (files.length === 0) return;

      await createFileAssets({
        files,
        itemType: ItemType.asset,
        insertPosition: {
          spaceId: space.id,
        },
      });
    },
    [canCreateContent, createFileAssets, space.id]
  );

  useEffect(() => {
    if (!canCreateContent) return;

    document.body.addEventListener("dragenter", handleDragEnter, { capture: true });
    document.body.addEventListener("dragover", handleDragOver, { capture: true });
    document.body.addEventListener("dragleave", handleDragLeave, { capture: true });
    document.body.addEventListener("drop", handleDrop, { capture: true });

    return () => {
      document.body.removeEventListener("dragenter", handleDragEnter, { capture: true });
      document.body.removeEventListener("dragover", handleDragOver, { capture: true });
      document.body.removeEventListener("dragleave", handleDragLeave, { capture: true });
      document.body.removeEventListener("drop", handleDrop, { capture: true });
    };
  }, [canCreateContent, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  const emptyState = useMemo(() => renderEmptyState(appliedFilters), [appliedFilters]);

  if (!canViewLibrary) {
    return (
      <Flex
        height="100vh"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        p="20px"
        data-testid="asset-library-forbidden">
        <Icon {...{ iconId: "info.lock-closed", size: "80", fill: "grayDark" }} />
        <Text {...{ as: "h2", font: "ui.title", mb: "xxs", color: "grayDarker" }}>Admin-only</Text>
        <Text
          {...{
            as: "h3",
            font: "ui.regular",
            color: "grayDarker",
            maxWidth: "400px",
          }}>
          You don&apos;t have access to the asset library for this space
        </Text>
        <Button
          {...{
            text: "Get help",
            onClick: () => openIntercom(),
            mt: "12px",
          }}
        />
      </Flex>
    );
  }

  return (
    <>
      <DropOverlay
        isVisible={isDragging && canCreateContent}
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={e => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <Box>
          <Icon iconId="empty-state.assets" size="230" fill="white" />
          <Text font="ui.subtitle" color="white">
            Drop to upload
          </Text>
        </Box>
      </DropOverlay>
      <Head>
        <title>{space.name} Library</title>
      </Head>
      <Flex flexDirection="column" width="100%" height="100%" overflow="hidden">
        <AssetLibraryHeader />
        <ScrollContainer height="100%">
          <AssetLibraryInspectorView assets={data?.assets || []} canEdit={canCreateAssets(space)}>
            <ScrollContainer key={rowSize} rowSize={rowSize}>
              <Flex
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                top="0"
                px="xl"
                py="m"
                backgroundColor="white"
                zIndex="100"
                position="sticky"
                borderBottom="default"
                data-inspector-clear="true">
                <AssetLibrarySelectAll data={data} />
              </Flex>
              <Box ref={tableWrapperRef}>
                <AssetLibraryActionMenu
                  open={contextMenuOpen}
                  position={{ x: contextMenuXPos, y: contextMenuYPos }}
                  close={handleContextMenuClose}
                  assets={data?.assets || []}
                />
                <DataTable<Asset>
                  borderTop={null}
                  columns={tableColumns}
                  data={data?.assets || []}
                  isLoading={isLoading}
                  hasMorePages={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  tableError={error}
                  selectionProps={selectionProps}
                  emptyState={emptyState}
                  sort={sort}
                  onSort={setSortBy}
                  onRowClick={selectAsset}
                  isLibraryView={true}
                />
              </Box>
            </ScrollContainer>
          </AssetLibraryInspectorView>
        </ScrollContainer>
      </Flex>
    </>
  );
};

export default AssetLibraryView;
